import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding-top: 70px;
  color: #fff;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

 

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
`;

const Subtitle = styled.p`
  font-size: 1.8rem;
  margin-bottom: 2rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  font-weight: 500;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 3rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(motion.div)`
  background-color: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  height: 100%;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #0d9488;
  text-align: center;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ServiceDescription = styled.p`
  color: #e0e0e0;
  font-size: 0.95rem;
  line-height: 1.7;
  margin-top: 0.5rem;
`;

const ServiceLink = styled(Link)`
  text-decoration: none;
  display: block;
  width: 100%;
  height: 100%;
`;

const PropertyTenant: React.FC = () => {
  const { language } = useLanguage();
  
  const services = [
    { 
      id: 'alloggi', 
      name: 'ALLOGGI', 
      link: '/accommodations',
      description: language === 'it' 
        ? 'Esplora la nostra selezione di alloggi di lusso disponibili per soggiorni brevi e lunghi in posizioni esclusive.'
        : 'Explore our selection of luxury accommodations available for short and long stays in exclusive locations.'
    },
    { 
      id: 'popup', 
      name: 'POP UP', 
      link: '/services/pop-up',
      description: language === 'it' 
        ? 'Spazi temporanei per eventi, showroom o concept store, completamente arredati e pronti all\'uso.'
        : 'Temporary spaces for events, showrooms or concept stores, fully furnished and ready to use.'
    },
    { 
      id: 'lavanderia', 
      name: 'LAVANDERIA', 
      link: '/services/laundry',
      description: language === 'it' 
        ? 'Servizi di lavanderia e noleggio biancheria di alta qualità per un soggiorno confortevole e senza pensieri.'
        : 'High-quality laundry and linen rental services for a comfortable and carefree stay.'
    },
    { 
      id: 'attrazioni', 
      name: 'ATTRAZIONI', 
      link: '/attractions',
      description: language === 'it' 
        ? 'Scopri le migliori attrazioni turistiche, culturali e naturalistiche nelle vicinanze del tuo alloggio.'
        : 'Discover the best tourist, cultural and naturalistic attractions near your accommodation.'
    },
    { 
      id: 'experience', 
      name: 'EXPERIENCE', 
      link: '/services/experiences',
      description: language === 'it' 
        ? 'Esperienze uniche e personalizzate per rendere memorabile il tuo soggiorno, dai tour enogastronomici alle attività culturali.'
        : 'Unique and personalized experiences to make your stay memorable, from food and wine tours to cultural activities.'
    },
    { 
      id: 'card', 
      name: 'CARD', 
      link: '/services/card',
      description: language === 'it' 
        ? 'Carte fedeltà e gift card che ti danno accesso a sconti e servizi esclusivi durante il tuo soggiorno.'
        : 'Loyalty cards and gift cards that give you access to exclusive discounts and services during your stay.'
    },
    { 
      id: 'trasporti', 
      name: 'TRASPORTI', 
      link: '/services/transportation',
      description: language === 'it' 
        ? 'Soluzioni di trasporto private e pubbliche per muoverti comodamente durante il tuo soggiorno.'
        : 'Private and public transportation solutions to move comfortably during your stay.'
    },
    { 
      id: 'attrezzatura', 
      name: 'ATTREZZATURA', 
      link: '/services/equipment',
      description: language === 'it' 
        ? 'Noleggio di attrezzature specializzate per le tue attività durante il soggiorno, dallo sport alle necessità familiari.'
        : 'Rental of specialized equipment for your activities during your stay, from sports to family needs.'
    },
    { 
      id: 'investimenti', 
      name: 'INVESTIMENTI', 
      link: '/services/investments',
      description: language === 'it' 
        ? 'Consulenza per opportunità di investimento immobiliare nella zona, dalla compravendita alle aste.'
        : 'Advice for real estate investment opportunities in the area, from buying and selling to auctions.'
    },
  ];

  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };

  const buttonVariants = {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    hover: { 
      scale: 1.05,
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
      y: -10
    },
    tap: { scale: 0.95 }
  };
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Trova un Alloggio | J-O-H' : 'Find Accommodation | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Scopri gli alloggi disponibili per affitti brevi e lunghi. Trova la soluzione perfetta per le tue esigenze.' 
            : 'Discover accommodations available for short and long-term rentals. Find the perfect solution for your needs.'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'Vuoi avere in affitto una casa?' : 'Looking to rent a house?'}</PageTitle>
          <Subtitle>{language === 'it' ? 'ECCO COSA POSSIAMO FARE PER TE' : 'HERE IS WHAT WE CAN DO FOR YOU'}</Subtitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <ServicesGrid>
            {services.map((service, index) => (
              service.link ? (
                <ServiceLink key={service.id} to={service.link}>
                  <ServiceCard
                    variants={buttonVariants}
                    initial="initial"
                    animate="animate"
                    whileHover="hover"
                    whileTap="tap"
                    transition={{ duration: 0.2, delay: index * 0.1 }}
                  >
                    <ServiceTitle>{service.name}</ServiceTitle>
                    <ServiceDescription>{service.description}</ServiceDescription>
                  </ServiceCard>
                </ServiceLink>
              ) : (
                <ServiceCard
                  key={service.id}
                  variants={buttonVariants}
                  initial="initial"
                  animate="animate"
                  whileHover="hover"
                  whileTap="tap"
                  transition={{ duration: 0.2, delay: index * 0.1 }}
                >
                  <ServiceTitle>{service.name}</ServiceTitle>
                  <ServiceDescription>{service.description}</ServiceDescription>
                </ServiceCard>
              )
            ))}
          </ServicesGrid>
        </ContentContainer>
      </ContentSection>
    </PageContainer>
  );
};

export default PropertyTenant; 